import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Mic, Square, Loader2, Volume2, Check, X, ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useRecorder, speak } from "@/lib/recorder";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Big = ({ children, className = "", ...p }) => (
  <button className={`w-full rounded-[2rem] p-8 text-left flex items-center gap-6 text-2xl md:text-3xl font-display font-semibold active:scale-[0.98] transition-transform ${className}`} {...p}>{children}</button>
);

const PendingLinks = ({ pending, onDone }) => (
  <div className="space-y-3">
    {pending.map((p) => (
      <div key={p.profile_id} className="rounded-3xl border-2 border-[#E07A5F] bg-white p-6" data-testid={`link-request-${p.profile_id}`}>
        <p className="text-xl">{p.from} vuole aiutarti con le pratiche. Vedrà i documenti che fotografi qui.</p>
        <div className="mt-4 flex gap-3">
          <button onClick={() => api.post(`/simple/links/${p.profile_id}/accept`).then(onDone)} className="rounded-full bg-[#1E3A8A] text-white px-6 py-3 text-lg flex items-center gap-2" data-testid={`link-accept-${p.profile_id}`}><Check /> Accetto</button>
          <button onClick={() => api.post(`/simple/links/${p.profile_id}/decline`).then(onDone)} className="rounded-full border px-6 py-3 text-lg flex items-center gap-2" data-testid={`link-decline-${p.profile_id}`}><X /> No</button>
        </div>
      </div>
    ))}
  </div>
);

export default function SimpleMode() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ caregivers: [], pending: [] });
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [voice, setVoice] = useState(null);
  const cam = useRef();
  const { recording, start, stop } = useRecorder();
  const load = useCallback(() => api.get("/simple/status").then(({ data }) => setStatus(data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const photo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("save", "true");
    fd.append("profile_id", "");
    setBusy("photo");
    setResult(null);
    try {
      const { data } = await api.post("/ai/analyze", fd);
      setResult(data.result);
      const r = data.result;
      speak(`${r.titolo}. ${r.riassunto}. ${(r.cosa_fare || []).slice(0, 3).join(". ")}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const toggleVoice = async () => {
    if (!recording) {
      try {
        await start();
      } catch {
        toast.error("Permetti l'uso del microfono");
      }
      return;
    }
    const file = await stop();
    const fd = new FormData();
    fd.append("file", file);
    setBusy("voice");
    try {
      const { data } = await api.post("/voice/ask", fd);
      setVoice(data);
      new Audio(`data:audio/mp3;base64,${data.audio_b64}`).play().catch(() => {});
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6] p-5 md:p-10 max-w-3xl mx-auto" data-testid="simple-mode">
      <div className="flex items-center justify-between">
        <p className="text-2xl font-display">Ciao {user?.name?.split(" ")[0]}</p>
        <Link to="/app" className="text-base text-slate-600 flex items-center gap-1" data-testid="simple-exit-link"><ArrowLeft className="h-4 w-4" /> Versione completa</Link>
      </div>
      {status.caregivers.length > 0 && <p className="mt-2 text-lg text-slate-600">Ti aiutano: {status.caregivers.join(", ")}. Riceveranno un avviso per ogni lettera.</p>}
      <div className="mt-8 space-y-5">
        <PendingLinks pending={status.pending} onDone={load} />
        <Big className="bg-[#1E3A8A] text-white" onClick={() => cam.current.click()} disabled={!!busy} data-testid="simple-photo-btn">
          {busy === "photo" ? <Loader2 className="h-12 w-12 animate-spin" /> : <Camera className="h-12 w-12" />}
          {busy === "photo" ? "Sto leggendo…" : "Fotografa la lettera"}
        </Big>
        <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={(e) => photo(e.target.files[0])} data-testid="simple-photo-input" />
        <Big className={recording ? "bg-red-600 text-white" : "bg-[#E07A5F] text-white"} onClick={toggleVoice} disabled={busy === "voice"} data-testid="simple-voice-btn">
          {busy === "voice" ? <Loader2 className="h-12 w-12 animate-spin" /> : recording ? <Square className="h-12 w-12" /> : <Mic className="h-12 w-12" />}
          {busy === "voice" ? "Sto pensando…" : recording ? "Tocca per finire" : "Fai una domanda a voce"}
        </Big>
      </div>
      {result && (
        <div className="mt-8 rounded-[2rem] bg-white border p-8 fade-up" data-testid="simple-result">
          <p className="text-3xl font-display font-semibold">{result.titolo}</p>
          <p className="mt-4 text-xl leading-relaxed">{result.riassunto}</p>
          {result.importo && <p className="mt-4 text-2xl font-semibold text-[#1E3A8A]">Da pagare: {result.importo}</p>}
          <ol className="mt-6 space-y-3 text-xl">{(result.cosa_fare || []).map((s, i) => <li key={i}><b>{i + 1}.</b> {s}</li>)}</ol>
          <button onClick={() => speak(`${result.riassunto}. ${(result.cosa_fare || []).join(". ")}`)} className="mt-6 rounded-full border px-6 py-3 text-lg flex items-center gap-2" data-testid="simple-read-btn"><Volume2 /> Leggimelo</button>
        </div>
      )}
      {voice && (
        <div className="mt-8 rounded-[2rem] bg-white border p-8 fade-up" data-testid="simple-voice-answer">
          <p className="text-lg text-slate-500">Hai chiesto: {voice.question}</p>
          <p className="mt-3 text-2xl leading-relaxed">{voice.answer}</p>
          <button onClick={() => new Audio(`data:audio/mp3;base64,${voice.audio_b64}`).play()} className="mt-6 rounded-full border px-6 py-3 text-lg flex items-center gap-2" data-testid="simple-replay-btn"><Volume2 /> Riascolta</button>
        </div>
      )}
    </div>
  );
}
